import { cva } from "class-variance-authority";
import { AlertOctagon, CheckCircle2, X } from "lucide-react";
import type React from "react";
import { cn } from "../../utils/cn";

type ProgressVariant = "default" | "error" | "success";

export interface ProgressProps {
  value: number;
  variant?: ProgressVariant;
  hideIcon?: boolean;
}

const VARIANT_ICONS: Record<ProgressVariant, React.ElementType> = {
  default: X,
  error: AlertOctagon,
  success: CheckCircle2,
};

const iconColorClasses = cva("shrink-0", {
  variants: {
    variant: {
      default: "text-primary",
      error: "text-red-600",
      success: "text-lime-500",
    },
  },
  defaultVariants: { variant: "default" },
});

const PROGRESS_MIN = 0;
const PROGRESS_MAX = 100;

const progressFillClasses = cva("", {
  variants: {
    variant: {
      default:
        "[&::-moz-progress-bar]:bg-primary [&::-webkit-progress-value]:bg-primary",
      error:
        "[&::-moz-progress-bar]:bg-red-600 [&::-webkit-progress-value]:bg-red-600",
      success:
        "[&::-moz-progress-bar]:bg-lime-500 [&::-webkit-progress-value]:bg-lime-500",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export function Progress(props: Readonly<ProgressProps>): React.ReactNode {
  const variant = props.variant ?? "default";
  const clampedValue = Math.min(
    PROGRESS_MAX,
    Math.max(PROGRESS_MIN, props.value)
  );

  const Icon = VARIANT_ICONS[variant];

  return (
    <div className="flex items-center gap-2">
      <progress
        className={cn(
          "block h-2.5 w-full appearance-none rounded-full bg-zinc-200",
          "[&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-zinc-200",
          "[&::-webkit-progress-value]:min-w-2.5 [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:transition-all [&::-webkit-progress-value]:duration-300",
          "[&::-moz-progress-bar]:min-w-2.5 [&::-moz-progress-bar]:rounded-full [&::-moz-progress-bar]:transition-all [&::-moz-progress-bar]:duration-300",
          progressFillClasses({ variant })
        )}
        max={PROGRESS_MAX}
        value={clampedValue}
      />
      {!props.hideIcon && (
        <Icon
          aria-hidden="true"
          className={iconColorClasses({ variant })}
          size={16}
        />
      )}
    </div>
  );
}
